import { useCallback, useEffect, useState } from "react";
import { Col, Row, Toast } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import {
  statementReportDelete,
  statementReportJsonGet,
  statementReportsGet,
  statementReportXlsxGet,
  statementTemplatesGet,
  statementReportPatch,
  statementTrialUsesGet,
} from "../../api";
import { NewStatementModal } from "./newstatementmodal";
import { fileLink } from "./filelink";
import { dateFormatter, dateGetter } from "../../common/utils";
import { getLocaleText } from "../../actions/datagrid";
import { PdfStatement } from "../../mgmtview/contracts/pdfstatement";
import { IconAddNew, IconDocx, IconPdf, IconXlsx, trashIconString } from "../../common/icons";
import { DocxStatement } from "../../mgmtview/contracts/docxstatement";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { activeIntegrationsSelector, authSelector } from "../../reducers";
import { getIntegrations } from "../../actions/organization/activeintegrations";
import { defaultColDef, gridSettings, loadViewState, saveViewState, sideBar } from "../../common/grid";
import { hrEsalary } from "../../main";
import { Button } from "../../common/Button.tsx";
import Chip from "../../common/chip/Chip.jsx";

const viewStateKey = "statementsViewState";

export function PersonnelStatements() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [templatesMap, setTemplatesMap] = useState({}); // map from json array
  const [reports, setReports] = useState(null); // json
  const [json, setJson] = useState(null); // for PDF / DOCX
  const [docx, setDocx] = useState(false); // present PDF or DOCX

  const auth = useSelector(authSelector);
  const activeIntegrations = useSelector(activeIntegrationsSelector);

  const [trialUses, setTrialUses] = useState(0);
  const fetchTrialUses = async () => setTrialUses(await statementTrialUsesGet());

  async function fetchData() {
    const templates = await statementTemplatesGet();
    const map = {};

    if (templates.length) {
      for (const template of templates) {
        map[template.id] = template.name;
      }
      setTemplatesMap(map);
    }

    setReports(await statementReportsGet());

    if (!activeIntegrations) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(auth.companyBusinessId));
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    } else if (!activeIntegrations.tokenOpenAi) {
      fetchTrialUses();
    }
  }

  useEffect(() => {
    fetchData();
  }, [activeIntegrations]);

  const statusMap = {
    DRAFT: { variant: "error", text: t("Draft", "Luonnos") },
    APPROVED: { variant: "success", text: t("Approved", "Hyväksytty") },
    PENDING: { variant: "warning", text: t("Pending", "Odottaa") },
  };

  const statusRenderer = (params, isOption = false) => {
    if (!params.value) {
      return "";
    }

    return (
      <Chip variant={statusMap[params.value].variant} isOption={isOption} >
        {statusMap[params.value].text}
      </Chip>
    );
  };

  const mutateColumn = useMutation({
    mutationFn: (json) => statementReportPatch(json.eventId, json),
    onError: ({ response }) => console.error("mutateColumn error", response),
  });

  const cellValueChanged = (params) =>
    mutateColumn.mutate({
      eventId: params.data.id,
      [params.colDef.field]: params.newValue,
    });

  const columnDefs = [
    {
      headerName: t("Created", "Luotu"),
      field: "updateInfo.createdTimestamp",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      width: 140,
    },
    {
      headerName: t("Status", "Tila"),
      field: "status",
      width: 125,
      filter: "agSetColumnFilter",
      editable: true,
      cellRenderer: statusRenderer,
      filterParams: {
        values: ["DRAFT", "APPROVED", "PENDING"],
        valueFormatter: (params) => statusMap[params.value]?.text,
        keyCreator: (params) => params.value,
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      singleClickEdit: true,
      cellEditorParams: {
        values: ["DRAFT", "APPROVED", "PENDING"],
        cellRenderer: (params) => statusRenderer(params, true),
      },
      onCellValueChanged: cellValueChanged,

      //valueGetter: statusGetter,

      //cellStyle: linkStyle,
      //onCellClicked: ({ data }) => fileLink(statementReportJsonGet(data.id)),
    },
    {
      headerName: "Doc",
      field: "doc",
      comparator: (_valA, _valB, nodeA, nodeB) => nodeA.data.id - nodeB.data.id, // sort latest statement id to top
      sort: "desc",
      width: 140,
      cellRenderer: ({ data }) => (
        <>
          <span
            style={{ cursor: "pointer" }}
            onClick={async () => {
              setDocx(false);
              setJson({
                ...(await (await statementReportJsonGet(data.id)).json()),
                language: data.language.toLowerCase(),
              });
            }}
          >
            <IconPdf />
          </span>
          <span
            style={{ marginLeft: "5px", cursor: "pointer" }}
            onClick={async () => {
              fileLink(statementReportXlsxGet(data.id));
            }}
          >
            <IconXlsx />
          </span>
          {auth.companyNumber === 7519 && (
            <span
              style={{ marginLeft: "5px", cursor: "pointer" }}
              onClick={async () => {
                window
                  .open(
                    "https://files.esalary.fi/33b95c93-e86e-54af-a23e-63d67f3349ed/Henkilöstökertomus-2024.docx",
                    "_blank",
                  )
                  .focus();
              }}
            >
              <IconDocx />
            </span>
          )}
        </>
      ),
    },
    // <a style={{marginLeft: "5px", cursor: "pointer"}} href="https://files.esalary.fi/33b95c93-e86e-54af-a23e-63d67f3349ed/Henkilöstökertomus-2024.docx" target="_blank" rel="noreferrer"><IconDocx /></a>
    // fileLink(fetch("https://files.esalary.fi/33b95c93-e86e-54af-a23e-63d67f3349ed/Henkilöstökertomus-2024.docx"));
    // window.open("https://files.esalary.fi/33b95c93-e86e-54af-a23e-63d67f3349ed/Henkilöstökertomus-2024.docx", "_blank").focus();
    // https://files.esalary.fi/33b95c93-e86e-54af-a23e-63d67f3349ed/Henkilöstökertomus-2024.docx
    //      onCellClicked: async params => docxLink(await statementReportJsonGet(params.data.id).json(), params.data[params.colDef.field]),
    //      onCellClicked: ({ data }) => fileLink(statementReportXlsxGet(data.id)),
    {
      headerName: "Nimi",
      field: "name",
      editable: true,
      onCellValueChanged: cellValueChanged,
    },
    {
      headerName: "Kommentti",
      field: "comment",
      editable: true,
      onCellValueChanged: cellValueChanged,
    },
    {
      headerName: "Alkaa",
      width: 140,
      field: "startDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: "Päättyy",
      width: 140,
      field: "endDate",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: "Tekijä",
      field: "personName",
    },
  ];

  const getContextMenuItems = useCallback((params) => {
    let items = [];

    const { status, name } = params.node.data;

    if (status === "DRAFT") {
      items.push({
        name: `${t("Delete", "Poista")} ${name}`,
        action: async () => {
          if (
            confirm(t("DeleteStatementConfirm", "Haluatko varmasti poistaa henkilöstökertomuksen {{name}}?", { name }))
          ) {
            await statementReportDelete(params.node.id);
            setReports(await statementReportsGet());
          }
        },
        icon: trashIconString,
      });
    }

    return items;
  }, [t]);

  const [showToast, setShowToast] = useState(true);
  const toggleShowToast = () => setShowToast(!showToast);

  return (
    <>
      {json !== null && (docx ? <DocxStatement data={json} /> : <PdfStatement data={json} setPdf={setJson} />)}
      {showModal && <NewStatementModal setShowModal={setShowModal} templatesMap={templatesMap} fetchData={fetchData} />}
      <Row>
        <Col sm="4">
          <h2 className="viewheading">{t("PersonnelStatements", "Henkilöstökertomus")}</h2>
        </Col>

        {location.hostname === hrEsalary && !activeIntegrations?.tokenOpenAi && (
          <Col sm="4">
            <Toast show={showToast} onClose={toggleShowToast} style={{ background: "#E6F3FE" }}>
              <Toast.Header>
                <span style={{ fontSize: "150%" }} className="me-1">
                  🛈
                </span>
                <strong className="me-auto">Koekäyttö</strong>
              </Toast.Header>
              <Toast.Body>
                <small>{trialUses}/10 henkilöstökertomusta luotu.</small>
              </Toast.Body>
            </Toast>
          </Col>
        )}

        <Col align="right">
          <Button testId="add-personnel" variant="primary" className="addbutton" onClick={() => setShowModal(true)}>
            <IconAddNew /> {t("AddPersonnelStatement", "Lisää Henkilöstökertomus")}
          </Button>
        </Col>
      </Row>

      <Row className="ag-theme-material styles-ag mt-3 flex-grow-1">
        <AgGridReact
          {...gridSettings}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={reports}
          sideBar={sideBar}
          getRowId={(params) => String(params.data.id)}
          suppressAggFuncInHeader
          animateRows
          enableCellTextSelection
          onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
          getContextMenuItems={getContextMenuItems}
          getLocaleText={(params) => getLocaleText(t, params)}
          //singleClickEdit
          //suppressCellFocus // does not work with editing
          stopEditingWhenCellsLoseFocus
          initialState={loadViewState(viewStateKey)}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
